import { ProductLineBadge } from '../components/ProductLineBadge';
import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Package,
  X,
  RefreshCw,
  AlertTriangle,
  Search,
  Grid3X3,
  Sparkles,
  Store,
  Lock,
  Layers,
  ChevronLeft,
} from 'lucide-react';
import { loadingService } from '../services/loadingService';
import { toastService } from '../services/toastService';
import { StaffPage } from '../components/staff/StaffChrome';
import { resolveAccent, tint } from '../utils/accentColor';
import { api } from '../services/apiService';
import { getIcon } from '../services/iconRegistry';
import IconPicker from '../components/IconPicker';
import ColorPicker from '../components/ColorPicker';
import { visiblePackages, tiersForPackage, planTierSave, runTierSave, familyOf, featureOwnersOutsideFamily } from '../services/packageTiers';
import type { FamilyLike, TierDraft } from '../services/packageTiers';
import { PackageTiersSection } from './packages/PackageTiersSection';
import { FeaturesByTier } from './packages/FeaturesByTier';
// Ruby AI family id + the ruby_tokens meter read/write (Plan B Task B6). Both
// modules already guard their own runtime shared-types import.
import { AI_FAMILY_ID } from './ai/rubyAi/useRubyAi';
import { includedRubyTokens, planTokenSaveOps, type FamilyTier } from './ai/rubyAi/catalogModel';

// ========================
// TYPES
// ========================

interface FeatureDefinition {
  id: string;
  name: string;
  /** 'coming_soon' = listed but not yet selectable. */
  status?: string;
  description: string;
  category: string;
  productType?: string;
  displayOrder?: number;
  route?: string;
  icon?: string;
  menuLabel?: string;
  roles?: string[];
  nativeHidden?: boolean;
  menuStyle?: any;
}

interface PackageType {
  id: string;
  name: string;
  description: string;
  productType: string | null;
  category: 'shared' | 'product-specific';
  status: 'active' | 'deprecated' | 'coming_soon';
  basePrice: number;
  isCore: boolean;
  /** Any tenant may buy it at list price. Absent = internal only, deals only. */
  marketplace?: boolean;
  features: string[];
  displayOrder?: number;
  menuGroupId?: string;
  icon?: string;
  colorType?: 'solid' | 'gradient';
  color?: string;
  gradientFrom?: string;
  gradientTo?: string;
  productLine?: 'rubytill' | 'rubyestate' | 'shared';
  createdAt: number;
  updatedAt: number;
  /** Package Tiers (2026-10-11): a package that is one tier of a family. */
  familyId?: string;
  tierRank?: number;
  role?: string;
  tierLabel?: string;
  /** Entitlement rules incl. the `ruby_tokens` meter (Plan B Task B6). GET
   * /admin/packages is a full table scan with no ProjectionExpression, so
   * this is present on every row that has it, same as any other field. */
  entitlements?: Array<Record<string, any>>;
  [k: string]: unknown;
}

interface MenuGroupOption {
  id: string;
  label: string;
  subgroups?: { id: string; label: string }[];
}

// ========================
// FALLBACK FEATURE DEFINITIONS
// ========================
// Used when API is loading or unavailable
const FALLBACK_FEATURE_DEFINITIONS: FeatureDefinition[] = [
  // Core features
  { id: 'dashboard', name: 'Dashboard', description: 'Main dashboard and overview', category: 'core' },
  { id: 'settings', name: 'Settings', description: 'Access to system settings and preferences', category: 'core' },
  { id: 'profile', name: 'Profile', description: 'User profile management', category: 'core' },
  { id: 'notifications', name: 'Notifications', description: 'Push and in-app notifications', category: 'core' },
  // Creator features
  { id: 'creator-studio', name: 'Creator Studio', description: 'Access to the AI-powered Creator Studio', category: 'creator' },
  { id: 'video-generation', name: 'Video Generation', description: 'AI video creation with templates', category: 'creator' },
  { id: 'composition-builder', name: 'Composition Builder', description: 'Build custom video compositions', category: 'creator' },
  { id: 'menu-boards', name: 'Menu Boards', description: 'Digital menu board generation', category: 'creator' },
  { id: 'public-shares', name: 'Public Shares', description: 'Share content publicly', category: 'creator' },
  { id: 'video-library', name: 'Video Library', description: 'Access to video library', category: 'creator' },
  { id: 'mini-menu-studio', name: 'Mini Menu Studio', description: 'Quick menu creation tool', category: 'creator' },
  // Team features
  { id: 'user-management', name: 'User Management', description: 'Manage team members and permissions', category: 'team' },
  { id: 'scheduling', name: 'Scheduling', description: 'Employee shift scheduling', category: 'team' },
  { id: 'routines', name: 'Routines', description: 'Daily task checklists and routines', category: 'team' },
  { id: 'training', name: 'Training', description: 'Staff training modules', category: 'team' },
  { id: 'employee-documents', name: 'Employee Documents', description: 'Document management for staff', category: 'team' },
  { id: 'time-off', name: 'Time Off', description: 'Leave and time-off requests', category: 'team' },
  { id: 'availability', name: 'Availability', description: 'Employee availability management', category: 'team' },
  // Inventory features
  { id: 'inventory', name: 'Inventory', description: 'Stock and inventory tracking', category: 'pos-menu' },
  { id: 'supplies', name: 'Supplies', description: 'Supply management and ordering', category: 'inventory' },
  { id: 'stock-reports', name: 'Stock Reports', description: 'Inventory reports and analytics', category: 'inventory' },
  { id: 'order-lists', name: 'Order Lists', description: 'Generate order lists', category: 'inventory' },
  // Menu features (merged under POS Retail Menu)
  { id: 'recipes', name: 'Recipes', description: 'Recipe management and costing', category: 'pos-menu' },
  { id: 'menu-hub', name: 'Menu Hub', description: 'Central menu management', category: 'pos-menu' },
  { id: 'categories', name: 'Categories', description: 'Menu categories management', category: 'pos-menu' },
  { id: 'menu-groups', name: 'Menu Groups', description: 'Group menus together', category: 'pos-menu' },
  { id: 'units', name: 'Units', description: 'Unit of measure management', category: 'pos-menu' },
  // POS features (AgenticPOS)
  { id: 'pos-products', name: 'POS Products', description: 'Product catalog for POS', category: 'pos' },
  { id: 'pos-categories', name: 'POS Categories', description: 'Category management for POS', category: 'pos' },
  { id: 'pos-modifiers', name: 'POS Modifiers', description: 'Product modifiers for POS', category: 'pos' },
  { id: 'pos-sales', name: 'POS Sales', description: 'Sales transactions', category: 'pos' },
  { id: 'pos-transactions', name: 'POS Transactions', description: 'Transaction history', category: 'pos' },
  { id: 'pos-reports', name: 'POS Reports', description: 'Sales reports and analytics', category: 'pos' },
  // POS Store Supply features
  { id: 'store-supply', name: 'Store Supplies', description: 'Track non-sellable items like change, bags, napkins', category: 'pos-store-supply' },
  // POS Clothing & Accessories product type
  { id: 'apparel', name: 'Clothing & Accessories', description: 'Colour × size products, per-colour photos and pricing, grid receiving and transfers', category: 'pos-apparel' },
  // POS Menu features (Retail Menu Manager)
  { id: 'pos-menu', name: 'Retail Menu Manager', description: 'Import recipes as POS-sellable products with modifiers and ingredient depletion', category: 'pos-menu' },
  { id: 'pos.menu.recipes', name: 'Menu Recipes', description: 'Browse and import recipes to POS', category: 'pos-menu' },
  { id: 'pos.menu.modifiers', name: 'Menu Modifiers', description: 'Modifier groups with per-variant pricing', category: 'pos-menu' },
  { id: 'pos.menu.ingredients', name: 'Menu Ingredients', description: 'View ingredient BOM from recipes', category: 'pos-menu' },
  { id: 'pos.menu.depletion', name: 'Menu Depletion', description: 'Auto-deplete ingredients and supplies on sale', category: 'pos-menu' },
  // POS Loyalty & Customers features
  { id: 'pos.customers.basic', name: 'Customers', description: 'Customer profiles and management', category: 'pos-loyalty' },
  { id: 'pos.customers.loyalty', name: 'Loyalty Earn Rules', description: 'Point earning rules, tiered brackets, category bonuses, events', category: 'pos-loyalty' },
  { id: 'pos.customers.rewards', name: 'Rewards Catalog', description: 'Redeemable rewards at different point tiers', category: 'pos-loyalty' },
  { id: 'pos.customers.coupons', name: 'Coupons', description: 'Coupon codes, bulk generation, campaigns', category: 'pos-loyalty' },
  { id: 'pos.customers.tiers', name: 'Membership Tiers', description: 'Bronze, Silver, Gold, Platinum tier configuration', category: 'pos-loyalty' },
  { id: 'pos.customers.portal', name: 'Customer Portal', description: 'Customer self-service portal (future)', category: 'pos-loyalty' },
  { id: 'pos.customers.import', name: 'Customer Import', description: 'CSV bulk import of customers with points', category: 'pos-loyalty' },
  { id: 'loyalty-vip', name: 'Loyalty & VIP Membership', description: 'VIP memberships and premium loyalty features', category: 'pos-loyalty' },
  { id: 'pos.button.customer', name: 'POS Customer Button', description: 'Customer lookup button on POS terminal', category: 'pos-loyalty' },
  { id: 'pos.button.rewards', name: 'POS Rewards Button', description: 'Rewards redemption button on POS terminal', category: 'pos-loyalty' },
  { id: 'pos.button.coupon', name: 'POS Coupon Button', description: 'Coupon code entry button on POS terminal', category: 'pos-loyalty' },
  // POS Oil Blending features (Module 29 — AgenticPOS)
  { id: 'pos.oil-blending.catalog', name: 'Oil Blending Catalog', description: 'Fragrances, carrier oils, and bottle catalog management', category: 'pos-oil-blending' },
  { id: 'pos.oil-blending.builder', name: 'Blend Builder', description: 'POS terminal custom blend builder screen', category: 'pos-oil-blending' },
  { id: 'pos.oil-blending.customer-portal', name: 'Customer Portal', description: 'Customer-facing QR/kiosk blend selection portal', category: 'pos-oil-blending' },
  { id: 'pos.oil-blending.signatures', name: 'Signature Library', description: 'Pre-built signature blend library', category: 'pos-oil-blending' },
  { id: 'pos.oil-blending.suggestions', name: 'Suggestion Engine', description: 'Mood/purpose-based blend recommendations', category: 'pos-oil-blending' },
  { id: 'pos.oil-blending.refills', name: 'BYOB Refills', description: 'Customer-owned bottle refill tracking', category: 'pos-oil-blending' },
  { id: 'pos.oil-blending.reports', name: 'Oil Blending Reports', description: 'Top fragrances, scent families, mood mix, refill activity reports', category: 'pos-oil-blending' },
  { id: 'pos.button.build-blend', name: 'POS Build Blend Button', description: 'Build Blend button on POS terminal', category: 'pos-oil-blending' },
  { id: 'pos.button.pending-blends', name: 'POS Pending Blends Button', description: 'Pending customer selections drawer button on POS terminal', category: 'pos-oil-blending' },
  { id: 'pos.button.customer-display', name: 'POS Customer Display Button', description: 'Launch customer-facing display button on POS terminal', category: 'pos-oil-blending' },
  // POS Services features (pkg_services)
  { id: 'pos.services.items', name: 'Service & rental items', description: 'Sell services and rentals; customer-required services', category: 'pos-services' },
  { id: 'pos.services.tickets', name: 'Drop-off job tickets', description: 'Claim tags, ready-by dates, pickup', category: 'pos-services' },
  { id: 'pos.services.appointments', name: 'Appointments & calendar', description: 'Staff and resource calendars', category: 'pos-services' },
  { id: 'pos.services.checkin', name: 'Check-in & walk-in queue', description: '“I’m here” link, counter QR, POS queue', category: 'pos-services' },
  { id: 'pos.services.online-booking', name: 'Online booking & pay', description: 'Book on the website with deposit, full or in-store payment', category: 'pos-services' },
  { id: 'pos.services.reminders', name: 'Reminders & notifications', description: 'Email and SMS confirmations, reminders, ready alerts', category: 'pos-services' },
  { id: 'pos.services.rentals', name: 'Rentals', description: 'Lend assets by hour/day/weekend with late fees', category: 'pos-services' },
  { id: 'pos.button.services', name: 'Services POS button', description: 'Register button that opens the Services hub', category: 'pos-services' },
  // Property features (AgenticRealestate)
  { id: 'properties', name: 'Properties', description: 'Property listings', category: 'property' },
  { id: 'listings', name: 'Listings', description: 'Manage property listings', category: 'property' },
  { id: 'leases', name: 'Leases', description: 'Lease management', category: 'property' },
  { id: 'tenants-mgmt', name: 'Tenant Management', description: 'Manage tenants', category: 'property' },
  { id: 'maintenance', name: 'Maintenance', description: 'Property maintenance tracking', category: 'property' },
];

const FEATURE_CATEGORIES: Record<string, { label: string; color: string }> = {
  core: { label: 'Core', color: 'bg-stone-200 text-stone-700' },
  creator: { label: 'Creator', color: 'bg-rose-100 text-rose-800' },
  team: { label: 'Team', color: 'bg-blue-100 text-blue-800' },
  inventory: { label: 'Inventory', color: 'bg-green-100 text-green-800' },
  menu: { label: 'Menu', color: 'bg-amber-100 text-amber-800' },
  pos: { label: 'POS', color: 'bg-emerald-100 text-emerald-800' },
  'pos-compliance': { label: 'POS Compliance', color: 'bg-red-100 text-red-800' },
  'pos-marketing': { label: 'POS Marketing', color: 'bg-pink-100 text-pink-800' },
  'pos-ecommerce': { label: 'POS E-Commerce', color: 'bg-rose-100 text-rose-800' },
  'pos-loyalty': { label: 'POS Loyalty', color: 'bg-yellow-100 text-yellow-800' },
  'pos-b2b': { label: 'POS B2B Wholesale', color: 'bg-orange-100 text-orange-800' },
  'pos-ai': { label: 'POS AI Agents', color: 'bg-rose-100 text-rose-800' },
  'pos-store-supply': { label: 'POS Store Supply', color: 'bg-teal-100 text-teal-800' },
  'pos-apparel': { label: 'POS Clothing & Accessories', color: 'bg-rose-100 text-rose-800' },
  'pos-menu': { label: 'POS Retail Menu', color: 'bg-amber-100 text-amber-800' },
  'pos-services': { label: 'POS Services', color: 'bg-teal-100 text-teal-800' },
  management: { label: 'Management', color: 'bg-cyan-100 text-cyan-800' },
  property: { label: 'Property', color: 'bg-sky-100 text-sky-800' },
  // Ruby Estate line — one feature list per Estate package (Package → Feature list → Features).
  estate: { label: 'Ruby Estate', color: 'bg-[#FFF1F3] text-[#9F1239]' },
  'estate-core': { label: 'Ruby Estate · Home, Bills & Insurance', color: 'bg-[#FFF1F3] text-[#9F1239]' },
  'estate-units': { label: 'Ruby Estate · Rental Unit Management', color: 'bg-[#FFF1F3] text-[#9F1239]' },
  'estate-leasing': { label: 'Ruby Estate · Applications & Leases', color: 'bg-[#FFF1F3] text-[#9F1239]' },
  'estate-maintenance': { label: 'Ruby Estate · Maintenance', color: 'bg-[#FFF1F3] text-[#9F1239]' },
  'estate-autopay': { label: 'Ruby Estate · Autopay', color: 'bg-[#FFF1F3] text-[#9F1239]' },
  'estate-website': { label: 'Ruby Estate · Online', color: 'bg-[#FFF1F3] text-[#9F1239]' },
  'pos-lottery': { label: 'POS Lottery', color: 'bg-lime-100 text-lime-800' },
  other: { label: 'Other', color: 'bg-stone-200 text-stone-700' },
};

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  deprecated: 'bg-red-100 text-red-800',
  coming_soon: 'bg-amber-100 text-amber-800',
};

/** Feature-list order in the editor: Core, the Ruby Till lists A–Z, then the Ruby Estate lists in package order. */
const ESTATE_ORDER = ['estate-core', 'estate-units', 'estate-leasing', 'estate-maintenance', 'estate-autopay', 'estate-website', 'estate'];
function categoryRank(category: string): number {
  if (category === 'core') return 0;
  const i = ESTATE_ORDER.indexOf(category);
  return i >= 0 ? 100 + i : 50;
}

function inferCategory(featureId: string): string {
  if (featureId.startsWith('estate.')) {
    const area = featureId.split('.')[1];
    return area && featureId.split('.').length > 2 ? `estate-${area}` : 'estate';
  }
  if (['compliance-safety'].includes(featureId) || featureId.startsWith('pos.compliance.')) return 'pos-compliance';
  if (['marketing', 'product-reviews', 'promotions', 'deals'].includes(featureId) || featureId.startsWith('pos.marketing.') || featureId.startsWith('pos.promos.')) return 'pos-marketing';
  if (['pos-ecommerce'].includes(featureId) || featureId.startsWith('pos.ecommerce.')) return 'pos-ecommerce';
  if (['loyalty-vip'].includes(featureId) || featureId.startsWith('pos.customers.')) return 'pos-loyalty';
  if (['pos-b2b'].includes(featureId) || featureId.startsWith('pos.b2b.')) return 'pos-b2b';
  if (['smart-image-search', 'ai-description', 'ai-insights', 'ai-careers-actions'].includes(featureId) || featureId.startsWith('pos.agents.')) return 'pos-ai';
  if (['store-supply'].includes(featureId)) return 'pos-store-supply';
  if (featureId === 'apparel') return 'pos-apparel';
  if (['pos-menu'].includes(featureId) || featureId.startsWith('pos.menu.')) return 'pos-menu';
  if (featureId.startsWith('pos.services.') || featureId === 'pos.button.services') return 'pos-services';
  if (featureId.startsWith('pos.')) return 'pos';
  if (['properties', 'listings', 'leases', 'tenants-mgmt', 'maintenance'].includes(featureId)) return 'property';
  if (['creator-studio'].includes(featureId)) return 'creator';
  if (['user-management', 'scheduling', 'routines', 'training'].includes(featureId)) return 'team';
  if (['menu-hub', 'recipes', 'inventory', 'categories', 'menu-groups', 'units'].includes(featureId)) return 'pos-menu';
  return 'other';
}

interface PackageFormData {
  name: string;
  description: string;
  features: string[];
  isCore: boolean;
  marketplace: boolean;
  displayOrder: number;
  menuGroupId: string;
  icon: string;
  colorType: 'solid' | 'gradient';
  color: string;
  gradientFrom: string;
  gradientTo: string;
  basePrice: string;
  productLine: 'rubytill' | 'rubyestate' | 'shared';
}

/**
 * Can this package go on the open shelf?
 *
 * A marketplace package is one a tenant adds to their own subscription with
 * nobody reviewing it, so it must carry a price. Mirrors the rule
 * `updatePackage` enforces server-side — the server is the one that counts,
 * this only keeps the form from offering a switch that would be refused.
 *
 * Takes the form's string price (dollars, as typed) so a half-typed "0." or an
 * empty box reads as unpriced rather than NaN.
 */
function sellablePrice(basePrice: string): boolean {
  return parseFloat(basePrice || '0') > 0;
}

const defaultFormData: PackageFormData = {
  name: '',
  description: '',
  features: [],
  isCore: false,
  marketplace: false,
  displayOrder: 99,
  menuGroupId: '',
  icon: '',
  colorType: 'solid',
  color: '#64748b',
  gradientFrom: '#e8365a',
  gradientTo: '#ec4899',
  basePrice: '0.00',
  productLine: 'rubytill',
};

// ========================
// Feature Selector Component
// ========================

interface FeatureSelectorProps {
  selectedFeatures: string[];
  onChange: (features: string[]) => void;
  features: FeatureDefinition[];
  isLoading?: boolean;
  usedByOtherPackages?: Record<string, string>; // featureId → package name
}

function FeatureSelector({ selectedFeatures, onChange, features, isLoading, usedByOtherPackages = {} }: FeatureSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  // Filter features based on search
  const getFilteredFeatures = () => {
    let filtered = features;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(f =>
        f.name.toLowerCase().includes(term) ||
        f.description.toLowerCase().includes(term) ||
        f.id.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const filteredFeatures = getFilteredFeatures();

  // Group features by category — uncategorized/'other' features fold into 'core'
  const featuresByCategory = filteredFeatures.reduce((acc, feature) => {
    const cat = feature.category && feature.category !== 'other' ? feature.category : 'core';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(feature);
    return acc;
  }, {} as Record<string, FeatureDefinition[]>);

  const toggleFeature = (featureId: string) => {
    if (usedByOtherPackages[featureId]) return; // Can't toggle features used by other packages
    if (features.find((f) => f.id === featureId)?.status === 'coming_soon') return; // Not built yet
    if (selectedFeatures.includes(featureId)) {
      onChange(selectedFeatures.filter(f => f !== featureId));
    } else {
      onChange([...selectedFeatures, featureId]);
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const selectAllInCategory = (category: string) => {
    const categoryFeatures = featuresByCategory[category]?.map(f => f.id) || [];
    const comingSoon = new Set((featuresByCategory[category] || []).filter((f) => f.status === 'coming_soon').map((f) => f.id));
    const availableFeatures = categoryFeatures.filter(f => !usedByOtherPackages[f] && !comingSoon.has(f));
    const allSelected = availableFeatures.every(f => selectedFeatures.includes(f));

    if (allSelected) {
      onChange(selectedFeatures.filter(f => !availableFeatures.includes(f)));
    } else {
      const newFeatures = new Set([...selectedFeatures, ...availableFeatures]);
      onChange(Array.from(newFeatures));
    }
  };

  if (isLoading) {
    return (
      <RubyLoader variant="section" label="Loading features..." className="bg-stone-100 rounded-lg border border-stone-300" />
    );
  }

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search features..."
          className="w-full pl-10 pr-4 py-2 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 text-sm placeholder:text-stone-500 focus:outline-none focus:border-admin-primary"
        />
      </div>

      {/* Selected count */}
      <div className="flex items-center justify-between text-xs text-stone-600">
        <span>{selectedFeatures.length} feature{selectedFeatures.length !== 1 ? 's' : ''} selected</span>
        {selectedFeatures.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="min-h-[32px] px-2 rounded font-semibold text-stone-700 hover:text-stone-900 hover:bg-stone-100"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Feature Categories */}
      <div className="max-h-[60vh] lg:max-h-[calc(90vh-360px)] overflow-y-auto space-y-2 pr-1">
        {Object.entries(featuresByCategory).sort(([a], [b]) => categoryRank(a) - categoryRank(b) || (FEATURE_CATEGORIES[a]?.label || a).localeCompare(FEATURE_CATEGORIES[b]?.label || b)).map(([category, categoryFeatures]) => {
          const categoryInfo = FEATURE_CATEGORIES[category] || { label: category, color: 'bg-stone-200 text-stone-700' };
          const isExpanded = expandedCategories.has(category);
          const selectedCount = categoryFeatures.filter(f => selectedFeatures.includes(f.id)).length;

          return (
            <div key={category} className="border border-stone-300 rounded-lg">
              {/* Category Header */}
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between px-3 py-2 bg-stone-100 hover:bg-stone-200 transition-colors"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Grid3X3 className="w-4 h-4 text-stone-500 shrink-0" />
                  <span className="text-[13px] px-2 py-0.5 rounded-md font-medium bg-white border border-stone-200 text-stone-800 whitespace-nowrap" title={categoryInfo.label}>
                    {categoryInfo.label}
                  </span>
                  <span className="text-sm text-stone-600 whitespace-nowrap">{categoryFeatures.length} feature{categoryFeatures.length === 1 ? "" : "s"}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {selectedCount > 0 && (
                    <span className="text-xs font-semibold text-stone-700 bg-stone-200 rounded-full px-2 py-0.5 whitespace-nowrap">{selectedCount} selected</span>
                  )}
                  <svg
                    className={`w-4 h-4 text-stone-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Feature List */}
              {isExpanded && (
                <div className="p-2 space-y-1 bg-stone-50">
                  {/* Select All */}
                  <button
                    type="button"
                    onClick={() => selectAllInCategory(category)}
                    className="w-full text-left px-2 py-1 text-xs font-semibold text-stone-700 hover:text-stone-900 hover:bg-stone-100 rounded transition-colors"
                  >
                    {categoryFeatures.every(f => selectedFeatures.includes(f.id)) ? 'Deselect all' : 'Select all'}
                  </button>

                  {categoryFeatures.map((feature) => {
                    const ownedBy = usedByOtherPackages[feature.id];
                    const comingSoon = feature.status === 'coming_soon';
                    const isDisabled = !!ownedBy || comingSoon;
                    return (
                      <div key={feature.id}>
                        <div className={`flex items-start gap-3 px-2 py-1.5 rounded transition-colors ${isDisabled ? 'opacity-50' : 'hover:bg-stone-100'}`}>
                          <input
                            type="checkbox"
                            checked={selectedFeatures.includes(feature.id) || !!ownedBy}
                            onChange={() => toggleFeature(feature.id)}
                            disabled={isDisabled}
                            className={`mt-0.5 w-4 h-4 rounded border-stone-300 bg-stone-100 focus:ring-admin-primary ${isDisabled ? 'cursor-not-allowed text-stone-600' : 'cursor-pointer text-admin-primary'}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${isDisabled ? 'text-stone-500' : 'text-gray-900'}`}>{feature.name}</span>
                              {ownedBy && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-600">{ownedBy}</span>
                              )}
                              {comingSoon && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">Coming soon</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(featuresByCategory).length === 0 && (
        <div className="p-4 text-center text-stone-600 text-sm">
          No features found. {searchTerm && 'Try a different search term.'}
        </div>
      )}
    </div>
  );
}

// ========================
// Main Component
// ========================

function PackagesPage() {
  const [packages, setPackages] = useState<PackageType[]>([]);
  const [features, setFeatures] = useState<FeatureDefinition[]>(FALLBACK_FEATURE_DEFINITIONS);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  /** Package id whose marketplace switch is mid-write, or null. */
  const [marketplaceSaving, setMarketplaceSaving] = useState<string | null>(null);
  const [forceDeleteInfo, setForceDeleteInfo] = useState<{ packageId: string; tenantsCount: number } | null>(null);
  const [editingPackage, setEditingPackage] = useState<PackageType | null>(null);
  const [formData, setFormData] = useState<PackageFormData>(defaultFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [families, setFamilies] = useState<FamilyLike[]>([]);
  /** False when the last `packageFamilies.list()` failed — the Tiers editor
   * section hides itself rather than risk saving against a stale/empty
   * families list (IMPORTANT 4). The grid keeps today's behaviour either way. */
  const [familiesLoaded, setFamiliesLoaded] = useState(true);
  const [tierDrafts, setTierDrafts] = useState<TierDraft[] | null>(null);
  /** Monthly Ruby Tokens draft per tier rank (1|2|3), '' = unlimited. Shown
   * only for the Ruby AI family or a tier that already carries the meter
   * (Plan B Task B6). */
  const [tokenDrafts, setTokenDrafts] = useState<Record<number, string>>({});

  // Fetch features from API and merge with fallback definitions
  const fetchFeatures = async () => {
    setFeaturesLoading(true);
    try {
      const response = await api.features.list();
      const apiFeatures: any[] = response.features || [];

      // Build a lookup from fallback definitions
      const fallbackMap = new Map(FALLBACK_FEATURE_DEFINITIONS.map(f => [f.id, f]));

      // Merge: API features get fallback name/description/category if missing
      const merged = apiFeatures.map((f: any) => {
        const fallback = fallbackMap.get(f.id);
        return {
          ...f,
          name: f.name || fallback?.name || f.menuLabel || f.id,
          description: f.description || fallback?.description || '',
          category: f.category || fallback?.category || inferCategory(f.id),
        };
      });

      // Add any fallback features not in API
      const apiIds = new Set(apiFeatures.map((f: any) => f.id));
      for (const fb of FALLBACK_FEATURE_DEFINITIONS) {
        if (!apiIds.has(fb.id)) {
          merged.push(fb);
        }
      }

      if (merged.length > 0) {
        setFeatures(merged);
      }
    } catch (error) {
      console.error('Failed to fetch features, using fallback:', error);
    } finally {
      setFeaturesLoading(false);
    }
  };

  // Fetch packages from API
  const fetchPackages = async () => {
    setIsLoading(true);
    try {
      const [resp, famResult] = await Promise.all([
        api.packages.list(),
        api.packageFamilies.list()
          .then((r) => ({ ok: true as const, families: r.families || [] }))
          .catch(() => ({ ok: false as const, families: [] })),
      ]);
      setPackages(resp.packages || []);
      setFamilies(famResult.families);
      setFamiliesLoaded(famResult.ok);
      if (!famResult.ok) toastService.error("Tiers unavailable — couldn't load package families");
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toastService.error('Failed to load packages');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
    fetchPackages();
  }, []);

  // Sort packages by displayOrder. Grouped tier rows that are not their
  // family's top are hidden here — they show inside the top tier's editor.
  const sortedPackages = visiblePackages([...packages], families).sort((a, b) => (a.displayOrder || 99) - (b.displayOrder || 99));

  /**
   * Monthly Ruby Tokens is shown only for the Ruby AI family, or a family
   * that already has a `ruby_tokens` meter on some tier (B6 ruling) — a
   * package family can carry the field without being the AI family if an
   * earlier save already put the meter on one of its tiers.
   */
  const editingFamily = editingPackage ? familyOf(editingPackage, families) ?? null : null;
  const editingFamilyPkgs = editingFamily
    ? packages.filter((p) => editingFamily.tierIds.includes(p.id))
    : editingPackage ? [editingPackage] : [];
  const showTokensField = !!editingPackage && (
    editingFamily?.id === AI_FAMILY_ID
    || editingPackage.familyId === AI_FAMILY_ID
    || editingFamilyPkgs.some((p) => includedRubyTokens(p as FamilyTier) !== undefined)
  );

  const handleRefresh = () => {
    loadingService.start();
    Promise.all([fetchFeatures(), fetchPackages()]).finally(() => loadingService.stop());
    toastService.info('Refreshing...');
  };

  const handleOpenCreate = () => {
    setFormData(defaultFormData);
    setEditingPackage(null);
    setTierDrafts(null);
    setTokenDrafts({});
    setShowCreateModal(true);
  };

  const handleOpenEdit = (pkg: PackageType) => {
    setFormData({
      name: pkg.name,
      description: pkg.description,
      features: pkg.features || [],
      isCore: pkg.isCore,
      marketplace: pkg.marketplace === true,
      displayOrder: pkg.displayOrder || 99,
      menuGroupId: pkg.menuGroupId || '',
      icon: pkg.icon || '',
      colorType: pkg.colorType || 'solid',
      color: pkg.color || '#64748b',
      gradientFrom: pkg.gradientFrom || '#e8365a',
      gradientTo: pkg.gradientTo || '#ec4899',
      basePrice: ((pkg.basePrice || 0) / 100).toFixed(2),
      productLine: pkg.productLine ?? 'rubytill',
    });
    setEditingPackage(pkg);
    const drafts = tiersForPackage(pkg, packages, families);
    setTierDrafts(drafts);
    const fam = familyOf(pkg, families);
    const famPkgs = fam ? packages.filter((p) => fam.tierIds.includes(p.id)) : [pkg];
    const seeded: Record<number, string> = {};
    drafts.forEach((d) => {
      const row = d.id ? famPkgs.find((p) => p.id === d.id) : undefined;
      const included = row ? includedRubyTokens(row as FamilyTier) : undefined;
      seeded[d.rank] = included == null ? '' : String(included);
    });
    setTokenDrafts(seeded);
    setShowCreateModal(true);
  };

  /**
   * Flip one package's shelf availability from the grid.
   *
   * Optimistic, and reverted on failure — this is a one-field write whose
   * result is a single switch position, so waiting on a round trip to move it
   * makes the grid feel broken while it is merely slow. `loadingService` is
   * deliberately NOT used: a full-page spinner for a toggle is worse than the
   * toggle. The in-flight id disables just the one control, so a double click
   * cannot race two writes against each other.
   *
   * The price rule is checked here too, but the server is what enforces it.
   */
  const handleToggleMarketplace = async (pkg: PackageType) => {
    if ((pkg.basePrice || 0) <= 0) {
      toastService.error('Set a monthly price before making this package available in the marketplace');
      return;
    }

    const next = !(pkg.marketplace === true);
    setMarketplaceSaving(pkg.id);
    setPackages((prev) => prev.map((p) => (p.id === pkg.id ? { ...p, marketplace: next } : p)));

    try {
      await api.packages.update(pkg.id, { marketplace: next });
      toastService.success(
        next
          ? `"${pkg.name}" is now available in the marketplace`
          : `"${pkg.name}" is now internal only`
      );
    } catch (error: any) {
      setPackages((prev) => prev.map((p) => (p.id === pkg.id ? { ...p, marketplace: !next } : p)));
      console.error('Failed to update package availability:', error);
      toastService.error(error?.message || 'Failed to update package availability');
    } finally {
      setMarketplaceSaving(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    loadingService.start();

    try {
      const payload: any = {
        name: formData.name,
        description: formData.description,
        features: formData.features,
        isCore: formData.isCore,
        // Always sent, never `|| undefined` — the update handler only writes
        // fields it can see, so turning the shelf OFF has to arrive as `false`.
        // ANDed with the price rule so an unpriced package cannot be saved on
        // the shelf even if the flag was set while it still had a price.
        marketplace: formData.marketplace && sellablePrice(formData.basePrice),
        displayOrder: formData.displayOrder,
        menuGroupId: formData.menuGroupId || undefined,
        icon: formData.icon || undefined,
        colorType: formData.colorType,
        color: formData.colorType === 'solid' ? formData.color : undefined,
        gradientFrom: formData.colorType === 'gradient' ? formData.gradientFrom : undefined,
        gradientTo: formData.colorType === 'gradient' ? formData.gradientTo : undefined,
        basePrice: Math.round(parseFloat(formData.basePrice || '0') * 100),
        productLine: formData.productLine,
      };

      if (editingPackage) {
        await api.packages.update(editingPackage.id, payload);
        // Exactly ONE toast reports the outcome of this whole save, whichever
        // branch below runs — a token-save problem must never fire its own
        // toast AND leave the generic "updated successfully" one to follow.
        let tokenIssueToast: string | null = null;
        if (tierDrafts && familiesLoaded) {
          const fam = familyOf(editingPackage, families) ?? null;
          const top = { ...tierDrafts[2], label: tierDrafts[2].label, priceDollars: formData.basePrice, features: formData.features };
          const ops = planTierSave({ ...editingPackage, ...payload }, [tierDrafts[0], tierDrafts[1], top], fam, packages);
          const createdByRank = await runTierSave(ops, api);
          if (showTokensField) {
            // Only bother with a family round trip when a draft actually
            // differs from the tier's last-known value — an untouched Tokens
            // field must never read as "changed" just because a sibling tier
            // was created above.
            const tokenDraftsChanged = tierDrafts.some((d) => {
              const row = d.id ? packages.find((p) => p.id === d.id) : undefined;
              const included = row ? includedRubyTokens(row as FamilyTier) : undefined;
              return (tokenDrafts[d.rank] ?? '') !== (included == null ? '' : String(included));
            });
            if (tokenDraftsChanged) {
              // Re-read the family for REAL tier ids — a tier just turned on
              // above only got a placeholder id until runTierSave created it.
              const famId = fam?.id ?? editingPackage.familyId;
              if (!famId) {
                tokenIssueToast = 'Package saved; Monthly Ruby Tokens not saved — this package has no tier family yet.';
              } else {
                try {
                  const freshFam = await api.packageFamilies.get(famId);
                  // Matched by each draft's OWN id (from tiersForPackage),
                  // falling back to the id runTierSave just created for a
                  // tier turned on in this save — never by position in
                  // family.tierIds and never by a tier's own tierRank field
                  // (final review I7/B6).
                  const tokenOps = planTokenSaveOps(freshFam.tiers ?? [], tierDrafts, tokenDrafts, createdByRank);
                  for (const op of tokenOps) {
                    await api.packages.update(op.tierId, { entitlements: op.entitlements });
                  }
                } catch (tokenError) {
                  console.error('Failed to save Monthly Ruby Tokens:', tokenError);
                  tokenIssueToast = 'Package saved, but Monthly Ruby Tokens could not be saved — try again from this editor.';
                }
              }
            }
          }
        }
        if (tokenIssueToast) toastService.error(tokenIssueToast);
        else toastService.success(`Package "${formData.name}" updated successfully`);
      } else {
        await api.packages.create(payload);
        toastService.success(`Package "${formData.name}" created successfully`);
      }

      setShowCreateModal(false);
      setEditingPackage(null);
      fetchPackages();
    } catch (error: any) {
      console.error('Failed to save package:', error);
      toastService.error(error?.message || 'Failed to save package');
      // IMPORTANT 2: a partial failure (e.g. the tier create succeeded but
      // the family link failed) can leave an orphaned tier row. Refetch and
      // rebuild the drafts from the server's real state before any retry,
      // so `tiersForPackage`'s orphan match (familyId+tierRank) picks the
      // row up and the retry UPDATES it instead of creating a duplicate.
      if (editingPackage) {
        try {
          const [resp, famResp] = await Promise.all([
            api.packages.list(),
            api.packageFamilies.list().then((r) => ({ ok: true as const, families: r.families || [] })).catch(() => ({ ok: false as const, families: [] })),
          ]);
          const freshPackages = resp.packages || [];
          setPackages(freshPackages);
          setFamilies(famResp.families);
          setFamiliesLoaded(famResp.ok);
          const freshEditing = freshPackages.find((p: PackageType) => p.id === editingPackage.id) ?? editingPackage;
          setTierDrafts(famResp.ok ? tiersForPackage(freshEditing, freshPackages, famResp.families) : null);
        } catch (refetchError) {
          console.error('Failed to refresh after a failed save:', refetchError);
        }
      }
    } finally {
      loadingService.stop();
    }
  };

  const handleDelete = async (pkgId: string, force?: boolean) => {
    loadingService.start();
    try {
      const result = await api.packages.delete(pkgId, force);
      if (result.assignmentsCanceled && result.assignmentsCanceled > 0) {
        toastService.success(`Package deleted. ${result.assignmentsCanceled} tenant assignment(s) canceled.`);
      } else {
        toastService.success('Package deleted successfully');
      }
      fetchPackages();
      setForceDeleteInfo(null);
    } catch (error: any) {
      console.error('Failed to delete package:', error);
      // Check if error is due to tenant assignments
      if (error?.message?.includes('assigned to tenants') && !force) {
        // Parse tenant count from error if available
        const match = error.message.match(/(\d+)\s+tenant/);
        const count = match ? parseInt(match[1], 10) : 1;
        setForceDeleteInfo({ packageId: pkgId, tenantsCount: count });
        setShowDeleteConfirm(null);
        return;
      }
      toastService.error(error?.message || 'Failed to delete package');
    } finally {
      loadingService.stop();
      setShowDeleteConfirm(null);
    }
  };

  // Get feature name from ID
  const getFeatureName = (featureId: string): string => {
    const feature = features.find(f => f.id === featureId);
    return feature?.name || featureId;
  };

  return (
    <StaffPage
      icon={Package}
      section="Platform"
      title="Packages"
      subtitle={`${sortedPackages.length} package${sortedPackages.length !== 1 ? 's' : ''} \u00b7 ${features.length} features available`}
      actions={(
        <>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-colors disabled:opacity-50 min-h-[40px]"
          >
            {isLoading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={handleOpenCreate}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 min-h-[40px] text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Create Package</span>
          </button>
        </>
      )}
    >

      {/* Info Banner */}
      {!featuresLoading && features.length === FALLBACK_FEATURE_DEFINITIONS.length && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <Sparkles className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-900 font-medium">Features loaded from fallback</p>
            <p className="text-xs text-amber-800 mt-1">
              Deploy the infrastructure and seed features to enable full dynamic feature management.
            </p>
          </div>
        </div>
      )}


      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedPackages.map((pkg) => {
          // Every card is painted in the package's OWN identity colour — the
          // band at full strength, the body white. See utils/accentColor.ts
          // for why the ink is derived rather than assumed.
          const a = resolveAccent(pkg);
          const Icon = getIcon(pkg.icon || '');
          return (
            <div
              key={pkg.id}
              className="group relative bg-white rounded-2xl border border-stone-200 overflow-hidden flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_36px_-12px_var(--pkg-glow)]"
              style={{ ['--pkg-glow' as any]: tint(a.from, 0.55) }}
            >
              {/* ---- Colour band: the package's own gradient/solid ---- */}
              <div className="relative px-5 py-4 overflow-hidden" style={{ background: a.band }}>
                {/* Light sweep + oversized icon watermark, so a solid colour
                    still has depth instead of reading as a flat rectangle. */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ backgroundImage: `radial-gradient(140px 90px at 85% -30%, ${tint(a.ink, 0.35)}, transparent 70%)` }}
                />
                <Icon
                  className="absolute -right-4 -bottom-6 w-28 h-28 pointer-events-none transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6"
                  style={{ color: a.ink, opacity: 0.13 }}
                />

                <div className="relative flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 backdrop-blur-sm"
                      style={{ background: tint(a.ink, 0.18), border: `1px solid ${tint(a.ink, 0.3)}` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: a.ink }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold leading-tight truncate" style={{ color: a.ink }}>{pkg.name}</h3>
                      <p className="text-[11px] font-mono truncate" style={{ color: a.ink, opacity: 0.72 }}>{pkg.id}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => handleOpenEdit(pkg)}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: a.ink, background: tint(a.ink, 0.12) }}
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {!pkg.isCore && (
                      <button
                        onClick={() => setShowDeleteConfirm(pkg.id)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ color: a.ink, background: tint(a.ink, 0.12) }}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ---- White body ---- */}
              <div className="p-5 flex flex-col flex-1">
                {/* Which product line(s) — before anyone opens the package. */}
                <ProductLineBadge line={pkg.productLine} className="mb-3" />
                <p className="text-sm text-stone-600 line-clamp-2 min-h-[40px]">{pkg.description}</p>

                {/* Monthly price — the package's own basePrice, stored in cents.
                    Same figure the edit form's "Monthly Price" field writes. */}
                <div className="flex items-end justify-between gap-3 mt-3 mb-4">
                  {/* Every package shows its three tiers. A package that has never been
                      tiered shows Basic/Pro as Off and its own price as the top tier. */}
                  <div className="grid grid-cols-3 gap-1.5 flex-1">
                    {tiersForPackage(pkg, packages, families).map((d) => (
                      <div key={d.rank} className={`rounded-lg px-2 py-1.5 ${d.rank === 3 ? 'bg-[#DC143C] text-white' : d.sell ? 'bg-rose-50' : 'border border-dashed border-stone-300 text-stone-400'}`}>
                        <div className="text-[11px]">{d.label}</div>
                        <div className="text-sm font-bold">{d.sell ? `$${d.priceDollars}` : 'Off'}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-wide ${statusColors[pkg.status || 'active']}`}>
                      {(pkg.status || 'active').replace('_', ' ')}
                    </span>
                    {pkg.isCore && (
                      <span className="text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-wide bg-stone-900 text-white">
                        Core
                      </span>
                    )}
                    {/* Product line: the Ruby Till / Ruby Estate badge at the top of the body. */}
                    {/* No marketplace chip here — the footer switch below says
                        the same thing and can also change it. Two places
                        showing one flag is two places to disagree. */}
                  </div>
                </div>

                {/* Features, chipped in the package's own colour */}
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-2">
                    {pkg.features?.length || 0} feature{(pkg.features?.length || 0) === 1 ? '' : 's'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(pkg.features || []).slice(0, 4).map((feature) => (
                      <span
                        key={feature}
                        className="text-[11px] px-2 py-1 rounded-md font-medium"
                        style={{ background: tint(a.from, 0.12), color: a.onWhite }}
                        title={getFeatureName(feature)}
                      >
                        {getFeatureName(feature)}
                      </span>
                    ))}
                    {(pkg.features || []).length > 4 && (
                      <span className="text-[11px] px-2 py-1 rounded-md font-medium bg-stone-100 text-stone-600">
                        +{pkg.features.length - 4} more
                      </span>
                    )}
                    {(pkg.features || []).length === 0 && (
                      <span className="text-[11px] text-stone-400 italic">No features yet</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 mt-4 border-t border-stone-100 flex items-center justify-between gap-3">
                  {/*
                    The same shelf flag the edit modal carries, switchable
                    without opening anything — setting availability across a
                    dozen packages is a pass over the grid, not twelve modals.

                    Writes ONLY `marketplace`; the update handler merges the
                    fields it is given, so nothing else on the package can be
                    clobbered by a click out here.
                  */}
                  {(() => {
                    const canSell = (pkg.basePrice || 0) > 0;
                    const on = pkg.marketplace === true && canSell;
                    const busy = marketplaceSaving === pkg.id;
                    return (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={on}
                        disabled={!canSell || busy}
                        onClick={() => handleToggleMarketplace(pkg)}
                        title={
                          !canSell
                            ? 'Set a monthly price before making this package available in the marketplace'
                            : on
                              ? 'On sale to any tenant — click to hold it back for deals only'
                              : 'Sold only through a deal — click to put it on the marketplace'
                        }
                        className={`inline-flex items-center gap-2 text-[11px] font-semibold rounded-lg px-2 py-1.5 transition-colors ${
                          busy ? 'opacity-60' : ''
                        } ${
                          !canSell
                            ? 'text-stone-400 cursor-not-allowed'
                            : on
                              ? 'text-emerald-700 hover:bg-emerald-50'
                              : 'text-amber-700 hover:bg-amber-50'
                        }`}
                      >
                        <span
                          className={`relative shrink-0 w-8 h-[18px] rounded-full transition-colors ${
                            !canSell ? 'bg-stone-300' : on ? 'bg-emerald-500' : 'bg-amber-400'
                          }`}
                        >
                          <span
                            className={`absolute top-[3px] left-[3px] w-3 h-3 rounded-full bg-white shadow transition-transform ${
                              on ? 'translate-x-[14px]' : 'translate-x-0'
                            }`}
                          />
                        </span>
                        {on ? (
                          <>
                            <Store className="w-3 h-3" /> Marketplace
                          </>
                        ) : (
                          <>
                            <Lock className="w-3 h-3" /> Internal
                          </>
                        )}
                      </button>
                    );
                  })()}

                  <button
                    onClick={() => handleOpenEdit(pkg)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                    style={{ color: a.onWhite, background: tint(a.from, 0.1) }}
                  >
                    Edit features
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {sortedPackages.length === 0 && !isLoading && (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
          <Package className="w-12 h-12 text-stone-600 mx-auto mb-3" />
          <p className="text-stone-600">No packages found</p>
          <p className="text-sm text-stone-500 mt-1">Create your first package to get started</p>
        </div>
      )}

      {isLoading && packages.length === 0 && (
        <RubyLoader variant="page" label="Loading packages..." />
      )}

      {/* Package editor — full-screen: pricing on top, details + features by tier below. */}
      {showCreateModal && (() => {
        const closeEditor = () => { setShowCreateModal(false); setEditingPackage(null); };
        const showTiers = !!(editingPackage && tierDrafts && familiesLoaded);
        const canSell = sellablePrice(formData.basePrice);
        const onShelf = formData.marketplace && canSell;
        const field = 'w-full h-9 px-3 rounded-lg border border-stone-300 bg-white text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-500';
        const label = 'block text-xs font-medium text-stone-600 mb-1';
        const pl = formData.productLine;
        const till = pl === 'rubytill' || pl === 'shared';
        const estate = pl === 'rubyestate' || pl === 'shared';
        const setLines = (t: boolean, e: boolean) => {
          if (!t && !e) return;
          setFormData({ ...formData, productLine: t && e ? 'shared' : t ? 'rubytill' : 'rubyestate' });
        };
        const lineOpt = (line: 'rubytill' | 'rubyestate', on: boolean, toggle: () => void) => (
          <button key={line} type="button" role="checkbox" aria-checked={on} aria-label={line === 'rubytill' ? 'Ruby Till' : 'Ruby Estate'} onClick={toggle}
            className={`min-h-[36px] pl-1.5 pr-2 rounded-full inline-flex items-center gap-2 ${on ? '' : 'opacity-50 hover:opacity-90'}`}>
            <span aria-hidden="true" className={`w-5 h-5 rounded-full inline-flex items-center justify-center border-2 ${on ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-stone-300'}`}>
              {on && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>}
            </span>
            <ProductLineBadge line={line} />
          </button>
        );
        return (
          <div className="fixed inset-0 z-50 bg-stone-100 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="pkg-editor-title">
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <header className="sticky top-0 z-20 bg-[#0c0a09] text-white">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <button type="button" onClick={closeEditor} aria-label="Back to packages"
                      className="w-10 h-10 shrink-0 grid place-items-center rounded-lg border border-stone-700 hover:bg-white/10">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="min-w-0">
                      <div className="text-[11px] text-stone-400 truncate">Packages › {formData.name || 'New package'}</div>
                      <h1 id="pkg-editor-title" className="text-base font-semibold leading-tight">{editingPackage ? 'Edit package' : 'Create package'}</h1>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={closeEditor} className="h-10 px-4 rounded-lg border border-stone-700 text-sm hover:bg-white/10">Cancel</button>
                    <button type="submit" className="h-10 px-5 rounded-lg bg-white text-stone-900 text-sm font-semibold hover:bg-stone-200">
                      {editingPackage ? 'Save package' : 'Create package'}
                    </button>
                  </div>
                </div>
                <div className="h-[3px]" style={{ background: 'linear-gradient(90deg,#9F1239,#DC143C 40%,rgba(220,20,60,0))' }} />
              </header>

              <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-5 space-y-5">
                {/* Pricing — first thing on the page */}
                <section className="bg-white border border-stone-200 rounded-xl p-4" aria-labelledby="pkg-pricing-h">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <h2 id="pkg-pricing-h" className="text-sm font-semibold text-stone-900">Pricing</h2>
                      <p className="text-xs text-stone-500">
                        {showTiers ? 'Rename any tier, set its monthly price, turn off a tier you don’t sell.' : 'Monthly list price of this package.'}
                      </p>
                    </div>
                    {/* Marketplace — who may buy it at the list price */}
                    <button type="button" role="switch" aria-checked={onShelf} disabled={!canSell}
                      onClick={() => setFormData({ ...formData, marketplace: !formData.marketplace })}
                      title={!canSell ? 'Set a monthly price first' : undefined}
                      className={`min-h-[40px] flex items-center gap-2.5 rounded-lg border px-3 text-xs font-medium disabled:cursor-not-allowed ${!canSell ? 'border-stone-200 text-stone-400' : onShelf ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                      {onShelf ? <Store className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                      <span className="text-left">
                        {onShelf ? 'Available in marketplace' : 'Internal only'}
                        <span className="block font-normal opacity-80">{!canSell ? 'Needs a price' : onShelf ? 'Any business can add it' : 'Sold through deals only'}</span>
                      </span>
                      <span className={`relative w-9 h-5 rounded-full ${!canSell ? 'bg-stone-300' : onShelf ? 'bg-emerald-500' : 'bg-amber-400'}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${onShelf ? 'left-[18px]' : 'left-0.5'}`} />
                      </span>
                    </button>
                  </div>
                  {showTiers ? (
                    <PackageTiersSection
                      drafts={tierDrafts!}
                      onChange={setTierDrafts}
                      topPrice={formData.basePrice}
                      onTopPrice={(v) => setFormData({ ...formData, basePrice: v })}
                      topCount={formData.features.length}
                      renderTierExtra={showTokensField ? (d) => (
                        <label className="block text-[11px] text-stone-500">
                          Monthly Ruby Tokens
                          <input inputMode="numeric" value={tokenDrafts[d.rank] ?? ''}
                            onChange={(e) => { const next = e.target.value.replace(/[^\d]/g, ''); setTokenDrafts((m) => ({ ...m, [d.rank]: next })); }}
                            placeholder="Unlimited" aria-label={`${d.label} monthly Ruby Tokens`}
                            className="mt-0.5 w-full h-8 rounded-md border border-stone-200 px-2 text-xs focus:outline-none focus:border-stone-500" />
                        </label>
                      ) : undefined}
                    />
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm text-stone-500">$</span>
                      <input inputMode="decimal" value={formData.basePrice}
                        onChange={(e) => setFormData({ ...formData, basePrice: e.target.value.replace(/[^0-9.]/g, '') })}
                        onBlur={() => setFormData({ ...formData, basePrice: (parseFloat(formData.basePrice || '0') || 0).toFixed(2) })}
                        aria-label="Monthly price" placeholder="0.00"
                        className="w-32 h-10 rounded-lg border border-stone-200 focus:border-stone-500 px-2 text-2xl font-bold tabular-nums text-stone-900 focus:outline-none" />
                      <span className="text-xs text-stone-500">/month</span>
                    </div>
                  )}
                  {showTiers && (
                    <p className="mt-3 text-[11px] text-stone-500">
                      A new tier price applies to every business on that tier (deals keep their discount). Removing a feature from a tier applies to new sign-ups only.
                    </p>
                  )}
                </section>

                <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)] items-start">
                  {/* Details */}
                  <section className="bg-white border border-stone-200 rounded-xl p-4 space-y-3.5 lg:sticky lg:top-[76px]" aria-labelledby="pkg-details-h">
                    <h2 id="pkg-details-h" className="text-sm font-semibold text-stone-900">Details</h2>
                    <div>
                      <label htmlFor="pkg-name" className={label}>Package name *</label>
                      <input id="pkg-name" type="text" required value={formData.name} placeholder="e.g., Team Management"
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={field} />
                    </div>
                    <div>
                      <label htmlFor="pkg-desc" className={label}>Description *</label>
                      <textarea id="pkg-desc" required rows={3} value={formData.description} placeholder="Brief description of the package"
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-500 resize-y" />
                    </div>
                    <div>
                      <span className={label}>Product line</span>
                      <div className="flex flex-wrap items-center gap-1 rounded-lg bg-stone-50 border border-stone-200 p-1">
                        {lineOpt('rubytill', till, () => setLines(!till, estate))}
                        {lineOpt('rubyestate', estate, () => setLines(till, !estate))}
                      </div>
                      <p className="text-[11px] text-stone-500 mt-1">{till && estate ? 'Shared — on both menus' : till ? 'Ruby Till only' : 'Ruby Estate only'}</p>
                    </div>
                    <div className="grid grid-cols-[96px_1fr] gap-3 items-end">
                      <div>
                        <label htmlFor="pkg-order" className={label}>Display order</label>
                        <input id="pkg-order" type="number" min="0" value={formData.displayOrder}
                          onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 99 })} className={field} />
                      </div>
                      <button type="button" role="switch" aria-checked={formData.isCore} onClick={() => setFormData({ ...formData, isCore: !formData.isCore })}
                        className="h-9 flex items-center justify-between gap-2 rounded-lg border border-stone-300 px-3 text-xs font-medium text-stone-700">
                        Core package
                        <span className={`relative w-9 h-5 rounded-full ${formData.isCore ? 'bg-stone-900' : 'bg-stone-300'}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${formData.isCore ? 'left-[18px]' : 'left-0.5'}`} />
                        </span>
                      </button>
                    </div>
                    <p className="text-[11px] text-stone-500 -mt-2">Lower order shows first. Core packages are included for every business.</p>
                    <div>
                      <span className={label}>Icon</span>
                      <IconPicker value={formData.icon} onChange={(icon) => setFormData({ ...formData, icon })} placeholder="Select icon" />
                    </div>
                    <div>
                      <span className={label}>Colour</span>
                      <ColorPicker
                        colorType={formData.colorType}
                        color={formData.color}
                        gradientFrom={formData.gradientFrom}
                        gradientTo={formData.gradientTo}
                        onColorTypeChange={(colorType) => setFormData({ ...formData, colorType })}
                        onColorChange={(color) => setFormData({ ...formData, color })}
                        onGradientChange={(gradientFrom, gradientTo) => setFormData({ ...formData, gradientFrom, gradientTo })}
                      />
                    </div>
                  </section>

                  {/* Features by tier */}
                  <FeaturesByTier
                    features={features}
                    categoryLabel={(c) => FEATURE_CATEGORIES[c]?.label || c || 'Other'}
                    categoryRank={categoryRank}
                    topFeatures={formData.features}
                    onTopFeatures={(next) => setFormData((f) => ({ ...f, features: next }))}
                    drafts={showTiers ? tierDrafts : null}
                    onDrafts={(next) => setTierDrafts(next)}
                    ownedElsewhere={featureOwnersOutsideFamily(packages, editingPackage, families)}
                    loading={featuresLoading}
                  />
                </div>
              </div>
            </form>
          </div>
        );
      })()}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-stone-200 w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Package?</h3>
            </div>
            <p className="text-stone-600 text-sm mb-6">
              This action cannot be undone. Tenants with this package will lose access to its features.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-gray-900 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Force Delete Confirmation Modal */}
      {forceDeleteInfo && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-stone-200 w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Package Has Active Assignments</h3>
            </div>
            <p className="text-stone-600 text-sm mb-4">
              This package is currently assigned to <span className="text-gray-900 font-semibold">{forceDeleteInfo.tenantsCount} tenant(s)</span>.
            </p>
            <p className="text-stone-600 text-sm mb-6">
              Force deleting will <span className="text-red-600 font-medium">cancel all assignments</span> and remove access to this package's features for those tenants.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setForceDeleteInfo(null)}
                className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-gray-900 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(forceDeleteInfo.packageId, true)}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                Force Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </StaffPage>
  );
}

export default PackagesPage;
